import { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext({});

export const useCurrency = () => useContext(CurrencyContext);

const EU_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

const CACHE_KEY = 'attributed_currency';

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(null);
  const [rate, setRate] = useState(null);
  const [symbol, setSymbol] = useState(null);

  useEffect(() => {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        setCurrency(data.currency);
        setRate(data.rate);
        setSymbol(data.symbol);
        return;
      } catch {}
    }

    const detect = async () => {
      try {
        const geoRes = await fetch('https://ipapi.co/json/');
        if (!geoRes.ok) return;
        const geo = await geoRes.json();
        const country = geo.country_code;

        let targetCurrency = null;
        let targetSymbol = null;

        if (EU_COUNTRIES.has(country)) {
          targetCurrency = 'EUR';
          targetSymbol = '€';
        } else if (country === 'GB') {
          targetCurrency = 'GBP';
          targetSymbol = '£';
        } else {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ currency: null, rate: null, symbol: null }));
          return;
        }

        const rateRes = await fetch('https://open.er-api.com/v6/latest/USD');
        if (!rateRes.ok) return;
        const rateData = await rateRes.json();
        const exchangeRate = rateData.rates?.[targetCurrency];
        if (!exchangeRate) return;

        const result = { currency: targetCurrency, rate: exchangeRate, symbol: targetSymbol };
        setCurrency(targetCurrency);
        setRate(exchangeRate);
        setSymbol(targetSymbol);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(result));
      } catch {
        // Silently fail — just show USD
      }
    };

    detect();
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, rate, symbol }}>
      {children}
    </CurrencyContext.Provider>
  );
};
